import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Lock, RefreshCw, ShieldAlert, X } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import type { C2Event, EventStatus, Incident } from '../../types';

type StatusChoice = EventStatus;

interface EventStatusModalProps {
  open: boolean;
  event: C2Event;
  onClose: () => void;
  onUpdated: (event: C2Event) => void;
}

const statusMeta: Record<StatusChoice, { label: string; color: string; border: string; bg: string }> = {
  open: {
    label: 'Open',
    color: 'text-emerald-400',
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/10',
  },
  supervision: {
    label: 'Supervision',
    color: 'text-amber-400',
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/10',
  },
  closed: {
    label: 'Close',
    color: 'text-gray-300',
    border: 'border-gray-500/30',
    bg: 'bg-gray-500/10',
  },
};

export default function EventStatusModal({ open, event, onClose, onUpdated }: EventStatusModalProps) {
  const { token } = useAppSelector((s) => s.auth);
  const [selected, setSelected] = useState<StatusChoice | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openIncidents, setOpenIncidents] = useState<Array<{ id: string; title: string; status: string }>>([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setNote('');
    setError(null);
    setOpenIncidents([]);
  }, [open, event.id]);

  useEffect(() => {
    if (!open || !token) return;
    setLoadingIncidents(true);
    fetch(`${API_BASE_URL}/incidents?eventId=${encodeURIComponent(event.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const list: Incident[] = d.incidents || [];
        setOpenIncidents(
          list
            .filter((i) => i.status !== 'closed')
            .map((i) => ({ id: i.id, title: i.title, status: i.status }))
        );
      })
      .catch(() => setOpenIncidents([]))
      .finally(() => setLoadingIncidents(false));
  }, [open, token, event.id]);

  const needsNote = useMemo(() => {
    if (!selected) return false;
    if (selected === 'supervision') return true;
    if (selected === 'closed') return true;
    if (selected === 'open' && event.status !== 'open') return true;
    return false;
  }, [selected, event.status]);

  const canClose = openIncidents.length === 0;

  if (!open) return null;

  const submit = async () => {
    if (!token || !selected) return;
    if (selected === 'closed' && !canClose) {
      setError(`Close blocked — ${openIncidents.length} incident(s) still open. Close them first.`);
      return;
    }
    if (needsNote && !note.trim()) {
      setError(
        selected === 'supervision'
          ? 'Enter why supervision is needed'
          : selected === 'closed'
            ? 'Enter why this event is being closed'
            : 'Enter why this event is being reopened'
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/events/${event.id}/set-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          status: selected,
          note: note.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.openIncidents) setOpenIncidents(data.openIncidents);
        throw new Error(data.error || 'Failed to update event status');
      }
      onUpdated(data.event);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update event status');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#0f1526] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="min-w-0">
            <p className="text-[10px] font-mono text-white/40">{event.id}</p>
            <h2 className="text-sm font-bold text-white truncate">{event.name}</h2>
            <p className="text-[11px] text-white/40 mt-0.5">
              Current status:{' '}
              <span className={cn('font-semibold uppercase', statusMeta[event.status].color)}>
                {event.status}
              </span>
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06]">
            <X className="w-4 h-4 text-white/40" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[11px] text-white/45">Choose the new event status:</p>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(statusMeta) as StatusChoice[]).map((key) => {
              const meta = statusMeta[key];
              const active = selected === key;
              const disabled = key === event.status;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setSelected(key);
                    setError(null);
                  }}
                  className={cn(
                    'py-3 rounded-xl border text-center transition-all',
                    active ? cn(meta.bg, meta.border) : 'border-white/[0.06] bg-white/[0.02]',
                    disabled && 'opacity-40 cursor-not-allowed'
                  )}
                >
                  <span className={cn('text-[11px] font-bold uppercase tracking-wide', meta.color)}>
                    {meta.label}
                  </span>
                </button>
              );
            })}
          </div>

          {selected === 'closed' && (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
              <div className="flex items-start gap-2">
                <Lock className="w-3.5 h-3.5 text-gray-300 mt-0.5" />
                <div className="text-[11px] text-white/60 leading-relaxed">
                  Closing locks all incident editing for this event and force-logs out every MDT still
                  signed in.
                </div>
              </div>
              {loadingIncidents ? (
                <p className="text-[10px] text-white/35 flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Checking incidents…
                </p>
              ) : canClose ? (
                <p className="text-[10px] text-emerald-400">All incidents are closed — ready to close event.</p>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {openIncidents.length} incident(s) still not closed:
                  </p>
                  <ul className="max-h-24 overflow-y-auto space-y-1">
                    {openIncidents.map((i) => (
                      <li key={i.id} className="text-[10px] text-white/50 font-mono">
                        {i.id} · {i.title} ({i.status})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="pt-2 space-y-2">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-300">
                  <Lock className="w-3.5 h-3.5" />
                  Close reason required
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Explain why this event is being closed…"
                  className="w-full px-3 py-2 rounded-lg text-xs text-white/90 placeholder-white/25 outline-none resize-none bg-black/30 border border-white/[0.08]"
                />
              </div>
            </div>
          )}

          {selected === 'supervision' && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-300">
                <ShieldAlert className="w-3.5 h-3.5" />
                Supervision reason required
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Explain why this event needs supervision…"
                className="w-full px-3 py-2 rounded-lg text-xs text-white/90 placeholder-white/25 outline-none resize-none bg-black/30 border border-white/[0.08]"
              />
            </div>
          )}

          {selected === 'open' && event.status !== 'open' && (
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300">
                <RefreshCw className="w-3.5 h-3.5" />
                Reopen justification required
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Explain why this event is being reopened…"
                className="w-full px-3 py-2 rounded-lg text-xs text-white/90 placeholder-white/25 outline-none resize-none bg-black/30 border border-white/[0.08]"
              />
            </div>
          )}

          {error && (
            <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold border border-white/[0.08] text-white/70 hover:bg-white/[0.04] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !selected || selected === event.status || (selected === 'closed' && !canClose) || (needsNote && !note.trim())}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? 'Updating…' : 'Confirm status'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
